import { useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCopy } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import {
  DIMENSIONS,
  DIMENSION_LABEL,
  convertToAllUnits,
  listUnits,
  type Dimension,
} from '../lib/unit';

const DEFAULT_FROM: Record<Dimension, string> = {
  length: 'm',
  mass: 'kg',
  temp: 'C',
  data: 'MiB',
  time: 's',
};

const DEFAULT_VALUE: Record<Dimension, number> = {
  length: 100,
  mass: 1,
  temp: 25,
  data: 1,
  time: 60,
};

function fmtNumber(n: number): string {
  if (!Number.isFinite(n)) return String(n);
  // Choose readable precision: small absolute → scientific; otherwise
  // a fixed precision that drops trailing zeros.
  const abs = Math.abs(n);
  if (abs !== 0 && (abs < 1e-4 || abs >= 1e15)) {
    return n.toExponential(6);
  }
  let s = n.toPrecision(12);
  // Drop scientific format if toPrecision picked it for small abs but
  // still readable.
  if (s.includes('e') || s.includes('E')) return s;
  // Remove trailing zeros after the decimal point.
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s;
}

/**
 * `/tools/convert/unit` — five-dimension unit converter.
 *
 * One dimension picker (length / mass / temp / data / time), one
 * "from unit" picker, one input field. The full table of units in
 * the chosen dimension is rendered below — entering a value updates
 * every row in real time.
 *
 * No "to" picker on purpose — it's faster to scan the whole table
 * than to repeatedly toggle a target. Per-row copy buttons make it
 * easy to grab any specific number.
 */
export default function UnitTool(): JSX.Element {
  const [dim, setDim] = useState<Dimension>('length');
  const [fromUnit, setFromUnit] = useState<string>(DEFAULT_FROM.length);
  const [raw, setRaw] = useState<string>(String(DEFAULT_VALUE.length));

  const units = useMemo(() => listUnits(dim), [dim]);
  const value = useMemo(() => {
    const trimmed = raw.trim();
    if (trimmed === '') return NaN;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : NaN;
  }, [raw]);

  const rows = useMemo(() => {
    if (!units.find((u) => u.id === fromUnit)) return [];
    return convertToAllUnits(dim, fromUnit, value);
  }, [dim, fromUnit, value, units]);

  const onDimChange = (d: Dimension): void => {
    setDim(d);
    const def = DEFAULT_FROM[d];
    setFromUnit(def);
    setRaw(String(DEFAULT_VALUE[d]));
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="unit"
        category="convert"
        description="长度 / 重量 / 温度 / 数据 / 时间 五维换算。输入一个值，下方所有同维度单位实时显示。温度走非线性 (offset)，数据维度同时含二进制 KiB 与十进制 KB。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            维度
          </span>
        </header>
        <div className="flex flex-wrap gap-1.5">
          {DIMENSIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => onDimChange(d)}
              className={clsx(
                'rounded border px-2 py-0.5 text-[12px] transition',
                d === dim
                  ? 'border-accent bg-accent/10 text-accent'
                  : 'border-line text-ink-muted hover:border-accent/40 hover:text-ink',
              )}
            >
              {DIMENSION_LABEL[d]}
            </button>
          ))}
        </div>
      </section>

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            输入
          </span>
        </header>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            inputMode="decimal"
            className="input flex-1 min-w-[180px] font-mono text-[13px]"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="输入数值（支持负数 / 小数 / 科学计数）"
          />
          <select
            className="input font-mono text-[13px]"
            value={fromUnit}
            onChange={(e) => setFromUnit(e.target.value)}
          >
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
        </div>
        {!Number.isFinite(value) && raw.trim() !== '' && (
          <div className="text-[12px] text-amber-600 dark:text-amber-400">
            无法解析为数值
          </div>
        )}
      </section>

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-1 overflow-auto">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">
          {DIMENSION_LABEL[dim]} · 全部单位
        </header>
        <ul className="divide-y divide-line/40">
          {rows.map((u) => (
            <UnitRow
              key={u.id}
              label={u.label}
              value={u.value}
              isInput={u.id === fromUnit}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

function UnitRow({
  label,
  value,
  isInput,
}: {
  label: string;
  value: number;
  isInput: boolean;
}): JSX.Element {
  const text = fmtNumber(value);
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* ignore */
    }
  };
  return (
    <li
      className={clsx(
        'group flex items-center gap-3 px-2 py-1.5 hover:bg-bg-soft',
        isInput && 'bg-accent/5',
      )}
    >
      <span className="w-44 shrink-0 text-[12px] text-ink-muted">{label}</span>
      <code className="min-w-0 flex-1 select-text break-all font-mono text-[13px] text-ink">
        {text}
      </code>
      <button
        type="button"
        onClick={onCopy}
        className="shrink-0 rounded p-0.5 text-ink-dim transition opacity-0 group-hover:opacity-100 hover:bg-bg-card hover:text-accent"
        title="复制"
      >
        {copied ? (
          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
        ) : (
          <ClipboardCopy className="h-3 w-3" />
        )}
      </button>
    </li>
  );
}
